import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  TrendingUp,
  MessageSquare,
  Users,
  Zap,
  Download,
  RefreshCw,
  AlertCircle,
  BarChart2,
  CheckCircle,
  Pause,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotService } from '../../../services/chatbotService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ── Shared chart options ──────────────────────────────────────────────────────
const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1f2937',
      titleColor: '#f9fafb',
      bodyColor: '#d1d5db',
      padding: 10,
      cornerRadius: 8,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#9ca3af', font: { size: 12 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f3f4f6' },
      ticks: {
        color: '#9ca3af',
        font: { size: 12 },
        stepSize: 1,
        precision: 0,
      },
    },
  },
};

// ── Empty placeholder ─────────────────────────────────────────────────────────
const EmptyChart = ({ height = 'h-64', label }) => (
  <div className={`flex items-center justify-center ${height} text-gray-400`}>
    <div className="text-center">
      <BarChart2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
      <p className="text-sm">{label}</p>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const ChatbotAnalytics = () => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState('30days');

  const [stats, setStats] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, chatbotsData, analyticsData] = await Promise.allSettled([
        chatbotService.getDashboardStats(),
        chatbotService.getChatbots(),
        chatbotService.getAnalytics(dateRange),
      ]);

      if (statsData.status === 'fulfilled') setStats(statsData.value);
      if (chatbotsData.status === 'fulfilled') setChatbots(chatbotsData.value || []);
      if (analyticsData.status === 'fulfilled') setAnalytics(analyticsData.value);
      else setAnalytics(null);

      if (statsData.status === 'rejected' && chatbotsData.status === 'rejected') {
        throw new Error('Failed to load analytics data');
      }
    } catch (err) {
      console.error('Analytics load error:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 text-primary-600 hover:underline"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }

  // ── Derived values ───────────────────────────────────────────────────────
  const metricCards = [
    {
      name: t('chatbot.totalConversations'),
      value: (stats?.totalConversations ?? 0).toLocaleString(),
      icon: MessageSquare,
      color: 'bg-blue-500',
    },
    {
      name: t('chatbot.totalMessages'),
      value: (stats?.totalMessages ?? 0).toLocaleString(),
      icon: Zap,
      color: 'bg-purple-500',
    },
    {
      name: t('chatbot.totalChatbots'),
      value: (stats?.totalChatbots ?? chatbots.length).toLocaleString(),
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: t('chatbot.activeChatbots'),
      value: (stats?.activeChatbots ?? chatbots.filter((b) => b.isActive).length).toLocaleString(),
      icon: TrendingUp,
      color: 'bg-orange-500',
    },
  ];

  // ── Chart data ───────────────────────────────────────────────────────────
  const trendPoints = (analytics?.conversationTrends || []).map((d) => ({
    label: d.date
      ? new Date(d.date).toLocaleDateString('en', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      : d.label,
    count: d.count ?? d.conversations ?? 0,
  }));

  const peakPoints = (analytics?.peakHours || []).map((d) => ({
    label: d.hour !== undefined ? `${d.hour}:00` : d.label,
    count: d.count ?? d.volume ?? 0,
  }));

  const trendChartData = {
    labels: trendPoints.map((d) => d.label),
    datasets: [
      {
        label: 'Conversations',
        data: trendPoints.map((d) => d.count),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.12)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#6366f1',
        borderWidth: 2,
      },
    ],
  };

  const peakChartData = {
    labels: peakPoints.map((d) => d.label),
    datasets: [
      {
        label: 'Volume',
        data: peakPoints.map((d) => d.count),
        backgroundColor: 'rgba(34,197,94,0.75)',
        hoverBackgroundColor: 'rgba(34,197,94,1)',
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const topIntents = analytics?.topIntents || [];

  const exportAnalytics = () => {
    const escape = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (cells) => cells.map(escape).join(',');
    const lines = [];

    // Summary
    lines.push(row(['Summary']));
    lines.push(row(['Metric', 'Value']));
    lines.push(row(['Total Conversations', stats?.totalConversations ?? 0]));
    lines.push(row(['Total Messages', stats?.totalMessages ?? 0]));
    lines.push(row(['Total Chatbots', stats?.totalChatbots ?? chatbots.length]));
    lines.push(row(['Active Chatbots', stats?.activeChatbots ?? chatbots.filter((b) => b.isActive).length]));
    lines.push('');

    // Conversation Trends
    if (trendPoints.length > 0) {
      lines.push(row(['Conversation Trends']));
      lines.push(row(['Date', 'Conversations']));
      trendPoints.forEach((d) => lines.push(row([d.label, d.count])));
      lines.push('');
    }

    // Peak Hours
    if (peakPoints.length > 0) {
      lines.push(row(['Peak Hours']));
      lines.push(row(['Hour', 'Volume']));
      peakPoints.forEach((d) => lines.push(row([d.label, d.count])));
      lines.push('');
    }

    // Bot Performance
    if (chatbots.length > 0) {
      lines.push(row(['Bot Performance']));
      lines.push(row(['Bot Name', 'Status', 'Conversations', 'Messages']));
      chatbots.forEach((bot) =>
        lines.push(row([bot.name, bot.isActive ? 'Active' : 'Paused', bot.totalConversations || 0, bot.totalMessages || 0]))
      );
      lines.push('');
    }

    // Top Intents
    if (topIntents.length > 0) {
      lines.push(row(['Top Intents']));
      lines.push(row(['Intent', 'Count']));
      topIntents.forEach((i) => lines.push(row([i.intent, i.count || 0])));
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${dateRange}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('chatbot.an_title')}</h1>
          <p className="text-gray-600 mt-1">{t('chatbot.an_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg border border-gray-200"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="7days">{t('chatbot.an_last7days')}</option>
            <option value="30days">{t('chatbot.an_last30days')}</option>
            <option value="90days">{t('chatbot.an_last90days')}</option>
          </select>
          <button className="btn btn-primary" onClick={exportAnalytics}>
            <Download className="w-5 h-5 mr-2" />
            {t('chatbot.an_exportReport')}
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${metric.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</div>
              <div className="text-sm text-gray-600">{metric.name}</div>
            </div>
          );
        })}
      </div>

      {/* Conversation Trends — Area Line Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">{t('chatbot.an_conversationTrends')}</h2>
        {trendPoints.length === 0 ? (
          <EmptyChart height="h-64" label={t('chatbot.an_noData')} />
        ) : (
          <div className="h-64">
            <Line data={trendChartData} options={baseOptions} />
          </div>
        )}
      </div>

      {/* Bot Performance + Top Intents */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Bot Performance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('chatbot.an_botPerformance')}</h2>
          {chatbots.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('chatbot.an_noChatbots')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {chatbots.map((bot) => {
                const StatusIcon = bot.isActive ? CheckCircle : Pause;
                const maxConvs = Math.max(...chatbots.map((b) => b.totalConversations || 0), 1);
                return (
                  <div key={bot.id} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <StatusIcon
                          className={`w-4 h-4 ${bot.isActive ? 'text-green-500' : 'text-yellow-500'}`}
                        />
                        <span className="font-medium text-gray-900">{bot.name}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {(bot.totalConversations || 0).toLocaleString()} {t('chatbot.an_convs')}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-500">Conversations</div>
                        <div className="font-bold text-blue-600">
                          {(bot.totalConversations || 0).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">{t('chatbot.cb_messages')}</div>
                        <div className="font-bold text-purple-600">
                          {(bot.totalMessages || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {chatbots.length > 1 && (
                      <div className="mt-3">
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div
                            className="bg-primary-500 h-1.5 rounded-full"
                            style={{
                              width: `${Math.round(((bot.totalConversations || 0) / maxConvs) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Intents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('chatbot.an_topIntents')}</h2>
          {topIntents.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <BarChart2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('chatbot.an_noIntents')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {topIntents.map((intent, index) => {
                const total = topIntents[0]?.count || 1;
                const pct = Math.round(((intent.count || 0) / total) * 100);
                return (
                  <div key={index} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">{intent.intent}</div>
                      <div className="text-sm text-gray-600">
                        {(intent.count || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-green-500 h-1.5 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Peak Hours — Bar Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">{t('chatbot.an_peakHours')}</h2>
        {peakPoints.length === 0 ? (
          <EmptyChart height="h-48" label={t('chatbot.an_noData')} />
        ) : (
          <div className="h-48">
            <Bar data={peakChartData} options={baseOptions} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotAnalytics;
